// ─────────────────────────────────────────────────────────────────────────────
// C7 (2026-08-13) — Export consolidado del panel Demográfico: UNA descarga con las hojas de
// detalle que hoy viven repartidas en varios componentes distintos («Brecha Generacional» de
// `BrechaGeneracionalTable`, «Heatmap OU x Fundamental» de `HeatmapTab`, «Red Cross-OU» de
// `NetworkTab`), más "Resumen Ejecutivo"/"Definiciones" (patrón común a los 9 Excel de Reportería,
// Tarea A3 — `exportExcelMultiSheet.ts`). No hay descarga servida por el backend: el Excel se arma
// client-side reutilizando los mismos datos que ya pintan las vistas (mismos hooks/queryKeys, sin
// llamada adicional cuando la caché de TanStack Query ya los tiene).
//
// Task 10 (2026-09-02) — se agrega el censo real por OU × banda generacional
// (`useRecognitionGenerationalDistribution`/`buildGenerationalDistributionMatrix`, Tasks 1-6) y se
// pintan con color real de Fundamental "Brecha Generacional" y "Heatmap OU x Fundamental"
// (`excelTintStyle`, Tasks 7-8) — mismo tinte que ya usan sus tablas en pantalla.
//
// 2026-09-03 (referencia visual del cliente, `04 - Demografico.xlsx`) — el libro queda en 5 hojas:
//   · "Resumen Ejecutivo" ES la tabla de Distribución Generacional (título + Periodo/Filtros/
//     Generado + tabla), no el resumen genérico de KPIs de `buildResumenEjecutivoSheet`. La pestaña
//     "Distribución Generacional" separada se eliminó: era la misma tabla dos veces.
//   · "Definiciones" gana la 4ª columna «Se compara contra» que pide la plantilla.
//   · "Red Cross-OU" pierde la columna «Volumen de reconocimientos» (el Fundamental dominante) en
//     el ARCHIVO; en pantalla `NetworkTab` sigue igual. Tampoco lleva el % Cross-OU (RF-153).
//
// 2026-09-28 (DEM-F5, REPORTERIA-REGLAS §10) — sale la hoja «Brecha Generacional» (y su fila en
// «Definiciones»): eran PROMEDIOS DE PULSO por generación, que no corresponden a esta sección. La
// tabla también salió de la pantalla. El libro queda en 4 hojas.
//
// 2026-09-04 (auditoría celda a celda de esa misma plantilla) — el archivo descargado calcaba las
// COLUMNAS pero no la PÁGINA. Lo que se corrigió:
//   · Las 5 hojas pasan a la paleta navy/crema de la plantilla (`TEMA_DEMOGRAFICO`) en vez del teal
//     de `02 - Campana brillamos juntos.xlsx`, que es de otro reporte.
//   · Las cuatro hojas que arrancaban directas en el encabezado ganan banner (título + subtítulo) y
//     nota al pie, como el "Resumen Ejecutivo" — toda la estructura vive en `demograficoExcel.ts`.
//   · Anchos de columna derivados del largo del encabezado: sin ellos, un Fundamental de 39
//     caracteres caía en una columna de 8.43 y el archivo salía ilegible.
//   · Alineación fijada por columna: las celdas tapadas por anonimato llevan un guion (texto, que
//     Excel alinea a la izquierda) y las de dato un número (que alinea a la derecha), así que la
//     misma columna salía con datos pegados a lados opuestos.
//   · El "Resumen Ejecutivo" lista las generaciones de la más veterana a la más joven, como la
//     plantilla (la PANTALLA conserva su orden: ver `buildGenerationalDistributionMatrix`).
//   · "Definiciones" y la columna «Nota» dejan de estar cableadas en español: salen en el idioma de
//     quien descarga, como el resto del libro.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRecognitionHeatmap, useRecognitionNetwork, useRecognitionCatalog, useRecognitionGenerationalDistribution,
  getFundamentalColor, getFundamentalName, recognitionReportsApi,
} from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { useOuNameResolver, usePersonNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { useEffectiveRoles } from '@/auth/RoleGuard';
import { excelColoresDemografico, excelTintStyle } from '@/utils/excelTheme';
import { exportarLibro } from '../exportarLibro';
import type { ExcelCellValue, ExcelSheetSpec, ExcelCellFillHint, ExcelCellStyleHint } from '@/utils/exportExcelMultiSheet';
import {
  anchoParaEncabezado, anchoParaTextoDeCelda, anchosParaEncabezados, columnaEtiqueta, columnaNumerica,
  columnaParrafo, columnaTexto, columnaTotal, construirHojaDemografica,
} from './demograficoExcel';
import type { HojaDemografica } from './demograficoExcel';
import {
  buildHeatmapMatrix, sumaFilaVisible, sumaColumnaVisible, maximoDeColumna, celdasFiltradasPorFundamental,
} from './heatmapMatrix';
import type { HeatmapAxis } from './heatmapMatrix';
import { buildGenerationalDistributionMatrix } from './generationalDistributionMatrix';
import type { NotaDeBanda } from './generationalDistributionMatrix';
import { construirFlujos } from './NetworkTab';
import {
  HEATMAP_ALLOWED_ROLES, INTENSIDAD_MINIMA_CON_DATO, INTENSIDAD_MINIMA_SIN_DATO, conFiltrosDeTira,
} from '../reportsShared';

const NOMBRE_ARCHIVO = 'reporteria-demografico';
const SIN_DATO = '—';

// Nombres de pestaña LITERALES (no `t()`): son los nombres exactos que pide el brief de la fase
// para "04 - Demografico.xlsx" — igual que "Resumen Ejecutivo"/"Definiciones" en
// `exportExcelMultiSheet.ts`, no cambian con el idioma de quien exporta. Excel además trunca
// nombres de pestaña a 31 caracteres; los títulos on-screen traducidos (`reports.heatmap.title`,
// etc.) pueden superarlo en varios idiomas, así que tampoco serían seguros de reutilizar aquí. El
// CONTENIDO de cada hoja sí sale traducido: el banner, los encabezados, los datos y las notas.
const NOMBRE_HOJA_RESUMEN = 'Resumen Ejecutivo';
const NOMBRE_HOJA_DEFINICIONES = 'Definiciones';
const NOMBRE_HOJA_HEATMAP = 'Heatmap OU x Fundamental';
const NOMBRE_HOJA_RED = 'Red Cross-OU';

/** Ancho mínimo de la columna A del "Resumen Ejecutivo": además del encabezado de la tabla lleva las
 *  etiquetas del bloque de metadatos, y «Filtros aplicados» es más larga que «Generación». */
const ANCHO_MINIMO_COLUMNA_ETIQUETAS = 22;

/** Ancho de las columnas del glosario de "Definiciones", medidos de la plantilla del cliente. */
const ANCHOS_DEFINICIONES = [30, 46, 52, 38];

interface Props {
  params: RecognitionReportParams;
  /** Idioma de los nombres de Fundamental en «Filtros aplicados» (antes llegaba en `analyticsFilters`). */
  language: string;
  /** default `true`: ver nota en `DailyTab.tsx`. */
  canExportData?: boolean;
}

export function DemograficoExportButton({ params, language, canExportData = true }: Readonly<Props>) {
  const { t } = useTranslation('recognition');

  // `HEATMAP_ALLOWED_ROLES` (`reportsShared.tsx`) es la MISMA lista que usa el `RoleGuard` de
  // `RecognitionAnalyticsView` sobre `HeatmapTab` — ver ahí el detalle de por qué incluye a
  // AdminBrillamos además de AdminGlobal. `useRecognitionHeatmap(params, false)` evita disparar una
  // petición para quien no tiene acceso al resultado; la hoja del Heatmap queda con solo cabecera
  // para AdminOUBrillamos (degradación honesta, no un error de export).
  const puedeVerHeatmap = useEffectiveRoles().some((role) => HEATMAP_ALLOWED_ROLES.includes(role));

  const distribucion = useRecognitionGenerationalDistribution(params);
  const heatmap = useRecognitionHeatmap(params, puedeVerHeatmap);
  const network = useRecognitionNetwork(params);
  const { data: catalog } = useRecognitionCatalog();
  const { resolveOuName } = useOuNameResolver();

  const lang = language || 'es';

  // Punto 6 (revisión final) — solo para «Filtros aplicados» del export (`conFiltrosDeTira`).
  const nombreDeFundamental = useMemo(() => {
    const porId = new Map((catalog ?? []).map((fundamental) => [fundamental.id, fundamental]));
    return (id: string) => {
      const fundamental = porId.get(id);
      return fundamental ? getFundamentalName(fundamental, lang) : id;
    };
  }, [catalog, lang]);
  const { resolvePersonName } = usePersonNameResolver(params.personaId ? [params.personaId] : []);
  // Mismo orden de columnas que `HeatmapTab` (orden de catálogo, no de aparición en `cells`) —
  // ambas superficies del mismo panel no pueden ordenar el mismo tipo de columna distinto.
  const fundamentalOrder = useMemo(
    () => new Map((catalog ?? []).map((fundamental) => [fundamental.id, fundamental.order])),
    [catalog],
  );

  const isLoading = distribucion.isLoading
    || (puedeVerHeatmap && heatmap.isLoading) || network.isLoading;

  /*
   * Fix final (revisión de rama) — la migración xlsx→exceljs volvió async a `exportMultiSheetExcel`
   * y `handleExport` quedó sin try/catch: un fallo (401/500) rechazaba la promesa SIN manejar — cero
   * mensaje, cero log. Mismo hallazgo ya corregido en `handleExportShine` (`ShineTab.tsx`, fix round
   * 1); el mensaje se REUSA de `reports.shine.exporting`/`exportError` (ya genérico) en vez de
   * duplicarlo.
   */
  const [exportandoDemografico, setExportandoDemografico] = useState(false);
  const [exportDemograficoError, setExportDemograficoError] = useState(false);

  /** Nota al pie compartida por las hojas de datos: por qué algunas celdas salen con un guion. */
  const notaDeAnonimato = t('reports.common.suppressedNote');

  /** Traduce el identificador canónico de `buildGenerationalDistributionMatrix` a texto visible. */
  function textoDeNota(nota: NotaDeBanda): string {
    if (nota === 'mayoria') return t('reports.generational.noteMajority');
    return nota === 'muestraPequena' ? t('reports.generational.noteSmallSample') : '';
  }

  /** Colores del catálogo por Fundamental — los mismos que pinta la pantalla. */
  const colorPorFundamental = useMemo(
    () => new Map(
      (catalog ?? []).map((fundamental, indice) => [fundamental.id, getFundamentalColor(fundamental, indice)]),
    ),
    [catalog],
  );

  /**
   * Hoja "Resumen Ejecutivo" — es la tabla de Distribución Generacional, no el resumen genérico de
   * KPIs que arma `buildResumenEjecutivoSheet` para los otros 8 Excel de Reportería.
   *
   * 🔴 Así lo pide la plantilla del cliente (`04 - Demografico.xlsx`): su primera pestaña se llama
   * "Resumen Ejecutivo" pero su contenido ES la composición de la plantilla por generación, OU y
   * tipo de colaborador — banner, bloque de metadatos (Periodo / Filtros aplicados / Generado) y la
   * tabla. Por eso esta hoja se construye aquí y no con el helper compartido, y por eso ya NO existe
   * una pestaña "Distribución Generacional" aparte: sería la misma tabla dos veces.
   */
  function buildResumenEjecutivoDemografico(): HojaDemografica {
    const { ous, filas } = buildGenerationalDistributionMatrix(
      distribucion.data?.bandas ?? [], distribucion.data?.ous ?? [],
    );
    const nombresOu = ous.map(resolveOuName);
    const periodo = [params.startDate, params.endDate].filter(Boolean).join(' – ') || SIN_DATO;
    // Los rótulos van traducidos (D-043, punto 6): ahora que el libro Demográfico entero sale en el
    // idioma de quien descarga, dejarlos en el español por omisión partiría esta línea en dos idiomas.
    const filtros = conFiltrosDeTira(
      params.ouIds?.length ? params.ouIds.map(resolveOuName).join(' · ') : SIN_DATO,
      params, nombreDeFundamental, resolvePersonName,
      { fundamental: t('reports.filters.fundamental'), persona: t('reports.filters.person') },
    );

    const encabezados = [
      t('reports.generational.colGeneration'),
      t('reports.generational.colPercentTotal'),
      t('reports.generational.colYearRange'),
      t('reports.generational.colPeople'),
      ...nombresOu,
      t('reports.generational.colFrontline'),
      t('reports.generational.colStaff'),
      t('reports.generational.colNote'),
    ];

    const tabla: ExcelCellValue[][] = [
      encabezados,
      ...filas.map((fila): ExcelCellValue[] => [
        fila.etiqueta,
        fila.suprimido ? SIN_DATO : Number(fila.pctDelTotal.toFixed(4)),
        fila.anios,
        fila.suprimido ? SIN_DATO : fila.total,
        ...ous.map((ouId) => (fila.suprimido ? SIN_DATO : (fila.porOu.get(ouId) ?? 0))),
        fila.suprimido ? SIN_DATO : fila.frontline,
        fila.suprimido ? SIN_DATO : fila.staff,
        textoDeNota(fila.nota),
      ]),
    ];

    const columnaNota = encabezados.length - 1;
    const columnWidths = anchosParaEncabezados(encabezados);
    columnWidths[0] = Math.max(columnWidths[0], ANCHO_MINIMO_COLUMNA_ETIQUETAS);

    return {
      nombre: NOMBRE_HOJA_RESUMEN,
      titulo: t('reports.generational.title'),
      subtitulo: t('reports.generational.exportSubtitle'),
      metadatos: [
        { etiqueta: t('reports.generational.metaPeriod'), valor: periodo },
        { etiqueta: t('reports.generational.metaFilters'), valor: filtros },
        { etiqueta: t('reports.generational.metaGenerated'), valor: new Date().toLocaleString(lang) },
      ],
      tabla,
      nota: notaDeAnonimato,
      columnWidths,
      columnas: [
        columnaEtiqueta(),
        // «% del total» en azul y negrita, como la plantilla. Sin `numFmt` ExcelJS pinta 0.25 como
        // "0" (formato General).
        {
          ...columnaNumerica(1, '0%'),
          fontColorArgb: excelColoresDemografico.porcentajeTexto,
          isBold: true,
        },
        columnaTexto(2),
        ...Array.from({ length: encabezados.length - 4 }, (_, indice) => columnaNumerica(indice + 3)),
        { ...columnaTexto(columnaNota), fontColorArgb: excelColoresDemografico.notaColumnaTexto },
      ],
    };
  }

  function buildHeatmap(): HojaDemografica {
    // Punto 2 (revisión final, 2026-09-03) — BLOQUEANTE: sin este recorte, con un Fundamental
    // puesto en la tira la pantalla (`HeatmapTab`) mostraba una sola columna y el Excel descargado
    // mostraba todas — el entregable divergía de lo que el cliente ve. Mismo recorte, compartido vía
    // `celdasFiltradasPorFundamental` (`heatmapMatrix.ts`), para que no puedan volver a desalinearse.
    const cells = celdasFiltradasPorFundamental(heatmap.data ?? [], params.fundamentalId);
    const { ous, funds, lookup } = buildHeatmapMatrix(cells, resolveOuName, fundamentalOrder);
    const etiquetaTotal = t('reports.heatmap.total');
    const encabezados = [t('reports.heatmap.ou'), ...funds.map((fund) => fund.name), etiquetaTotal];

    const filasDeOu = ous.map((ou): ExcelCellValue[] => {
      const { suma, hayTapadas } = sumaFilaVisible(ou.id, funds, lookup);
      return [
        ou.name,
        ...funds.map((fund) => {
          const cell = lookup.get(`${ou.id}::${fund.id}`);
          return !cell || cell.suprimido ? SIN_DATO : cell.count;
        }),
        hayTapadas ? `≥ ${suma}` : suma,
      ];
    });

    // Fila «Total» al pie, como la plantilla del cliente — misma regla que la columna Total que ya
    // existía: una celda tapada por anonimato NO se suma como cero, así que el total se marca con
    // «≥» cuando hay celdas ocultas (afirmar un total exacto que no se puede afirmar sería mentir).
    const filaTotal: ExcelCellValue[] = ous.length > 0
      ? [
        etiquetaTotal,
        ...funds.map((fund) => {
          const { suma, hayTapadas } = sumaColumnaVisible(fund.id, ous, lookup);
          return hayTapadas ? `≥ ${suma}` : suma;
        }),
        totalGeneralDeHeatmap(ous, funds, lookup),
      ]
      : [];

    const tabla: ExcelCellValue[][] = [
      encabezados,
      ...filasDeOu,
      ...(filaTotal.length > 0 ? [filaTotal] : []),
    ];

    // Mismo tinte que pinta `HeatmapTab` en pantalla: color del Fundamental de esa columna,
    // intensidad relativa al máximo de la columna (`maximoDeColumna`, ya exportado de `heatmapMatrix.ts`).
    const cellFills: ExcelCellFillHint[] = [];
    ous.forEach((ouActual, filaIndice) => {
      funds.forEach((fund, columnaIndice) => {
        const cell = lookup.get(`${ouActual.id}::${fund.id}`);
        const color = colorPorFundamental.get(fund.id) ?? '#6b7280';
        const conDato = !!cell && !cell.suprimido;
        const ratio = conDato ? cell.count / maximoDeColumna(fund.id, ous, lookup) : 0;
        const piso = conDato ? INTENSIDAD_MINIMA_CON_DATO : INTENSIDAD_MINIMA_SIN_DATO;
        const { fillArgb, fontArgb } = excelTintStyle(color, ratio, piso);
        cellFills.push({ row: filaIndice + 1, column: columnaIndice + 1, fillArgb, fontArgb });
      });
    });

    const indiceColumnaTotal = encabezados.length - 1;
    const cellStyles: ExcelCellStyleHint[] = [
      // Fila «Total»: navy sólido de lado a lado, como la plantilla.
      ...(filaTotal.length > 0
        ? encabezados.map((_, columnaIndice): ExcelCellStyleHint => ({
          row: filasDeOu.length + 1,
          column: columnaIndice,
          fillArgb: excelColoresDemografico.totalFondo,
          fontColorArgb: excelColoresDemografico.totalTexto,
          isBold: true,
          horizontal: columnaIndice === 0 ? 'left' : 'center',
        }))
        : []),
    ];

    return {
      nombre: NOMBRE_HOJA_HEATMAP,
      titulo: t('reports.heatmap.title'),
      subtitulo: t('reports.heatmap.subtitle'),
      tabla,
      nota: notaDeAnonimato,
      columnWidths: anchosParaEncabezados(encabezados),
      columnas: [
        columnaEtiqueta(),
        ...funds.map((_, indice) => columnaNumerica(indice + 1)),
        columnaTotal(indiceColumnaTotal),
      ],
      cellFills,
      cellStyles,
      filasDeTotales: filaTotal.length > 0 ? 1 : 0,
    };
  }

  /**
   * 🔴 Sin la columna «Volumen de reconocimientos» (`reports.network.colDominantFundamental`, la
   * del Fundamental dominante): el cliente la pidió fuera del Excel el 2026-09-03. En PANTALLA se
   * conserva tal cual — la barra coloreada de `NetworkTab` no se toca, esto solo acota qué columnas
   * viajan al archivo descargado. Tampoco se agrega aquí el % Cross-OU agregado (RF-153).
   */
  function buildRed(): HojaDemografica {
    const { flujos } = construirFlujos(network.data?.aristas ?? [], resolveOuName);
    const encabezados = [t('reports.network.colFlow'), t('reports.network.colRecognitions')];
    const tabla: ExcelCellValue[][] = [
      encabezados,
      ...flujos.map((flujo): ExcelCellValue[] => {
        const sufijoCruce = flujo.crossOu ? ` · ${t('reports.network.legendCross')}` : '';
        return [`${flujo.de} → ${flujo.a}${sufijoCruce}`, flujo.recos];
      }),
    ];

    // La columna de flujos lleva «OU origen → OU destino · Flujo cross-OU»: mucho más largo que su
    // encabezado, así que se dimensiona con el texto real más largo y no con el del encabezado.
    const textoMasLargo = tabla.slice(1).reduce(
      (masLargo, fila) => (String(fila[0]).length > masLargo.length ? String(fila[0]) : masLargo),
      encabezados[0],
    );

    return {
      nombre: NOMBRE_HOJA_RED,
      titulo: t('reports.network.title'),
      subtitulo: t('reports.network.subtitle'),
      tabla,
      nota: notaDeAnonimato,
      columnWidths: [anchoParaTextoDeCelda(textoMasLargo), anchoParaEncabezado(encabezados[1])],
      columnas: [columnaEtiqueta(), columnaNumerica(1)],
    };
  }

  /**
   * Hoja "Definiciones" — glosario "Dato | Qué es | Cómo se calcula | Se compara contra".
   *
   * No usa `buildDefinicionesSheet` (`exportExcelMultiSheet.ts`, compartido por los otros 8
   * reportes): la plantilla del Demográfico le pone banner y nota al pie como a cualquier otra hoja
   * del libro, y sus textos salen traducidos. Sólo se documenta lo que el archivo REALMENTE trae —
   * la plantilla de referencia define además "Cross-OU %" y "Datos Demo", que son de su maqueta y no
   * existen en este export.
   */
  function buildDefiniciones(): HojaDemografica {
    const encabezados = [
      t('reports.definitions.colData'),
      t('reports.definitions.colWhat'),
      t('reports.definitions.colHow'),
      t('reports.definitions.colVs'),
    ];
    const tabla: ExcelCellValue[][] = [
      encabezados,
      [
        `${t('reports.generational.colPercentTotal')} (${t('reports.generational.title')})`,
        t('reports.definitions.percentTotalWhat'),
        t('reports.definitions.percentTotalHow'),
        t('reports.definitions.percentTotalVs'),
      ],
      [
        t('reports.heatmap.title'),
        t('reports.definitions.heatmapWhat'),
        t('reports.definitions.heatmapHow'),
        t('reports.definitions.heatmapVs'),
      ],
      [
        t('reports.network.title'),
        t('reports.definitions.networkWhat'),
        t('reports.definitions.networkHow'),
        t('reports.definitions.networkVs'),
      ],
    ];

    return {
      nombre: NOMBRE_HOJA_DEFINICIONES,
      titulo: t('reports.definitions.title'),
      subtitulo: t('reports.definitions.subtitle'),
      tabla,
      nota: t('reports.context.auditText'),
      columnWidths: [...ANCHOS_DEFINICIONES],
      columnas: [
        { ...columnaEtiqueta(), vertical: 'top', hasWrapText: true },
        columnaParrafo(1),
        columnaParrafo(2),
        columnaParrafo(3),
      ],
    };
  }

  async function handleExport() {
    setExportandoDemografico(true);
    setExportDemograficoError(false);
    try {
      const hojas = [
        buildResumenEjecutivoDemografico(),
        buildDefiniciones(),
        buildHeatmap(),
        buildRed(),
      ];

      // Filas de DATOS (sin cabecera) de las 3 tablas de datos — es lo que se audita como
      // `rowCount`: cuánto contenido real salió, no cuántas hojas ni cuántas celdas. "Definiciones"
      // es glosario, no dato, así que no cuenta.
      const rowCount = hojas
        .filter((hoja) => hoja.nombre !== NOMBRE_HOJA_DEFINICIONES)
        .reduce(
          (total, hoja) => total + Math.max(0, hoja.tabla.length - 1 - (hoja.filasDeTotales ?? 0)),
          0,
        );

      const sheets: ExcelSheetSpec[] = hojas.map(construirHojaDemografica);
      await exportarLibro({ fileName: NOMBRE_ARCHIVO, sheets });

      // Fire-and-forget (Paso 4 del brief): NUNCA bloquea la descarga, que ya se completó arriba. Si
      // falla, se loguea (guard DEV) — la bitácora perdió UN registro, no la descarga del usuario.
      recognitionReportsApi.auditDemograficoExport(params.ouIds?.join(',') || params.ou, rowCount).catch((error: unknown) => {
        if (import.meta.env.DEV) console.error('No se pudo registrar la auditoría del export Demográfico:', error);
      });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export Demográfico:', error);
      setExportDemograficoError(true);
    } finally {
      setExportandoDemografico(false);
    }
  }

  if (!canExportData) return null;

  return (
    <div className="rr-actions">
      <button type="button" className="rr-btn" onClick={handleExport} disabled={isLoading || exportandoDemografico}>
        <i
          className={exportandoDemografico ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
          aria-hidden="true"
        />{' '}
        {exportandoDemografico ? t('reports.shine.exporting') : t('reports.common.exportExcel')}
      </button>

      {/* Mismo hallazgo que `handleExportShine` (fix round 1, `ShineTab.tsx`): sin esto un fallo del
          export quedaba en silencio, sin mensaje ni log. */}
      {exportDemograficoError && (
        <p className="rr-state rr-state--error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
        </p>
      )}
    </div>
  );
}

/**
 * Total general de la matriz del Heatmap (esquina inferior derecha): suma de los totales visibles por
 * columna, marcada con «≥» si alguna celda quedó tapada por el umbral de anonimato — misma regla que
 * `sumaFilaVisible`/`sumaColumnaVisible`, aplicada una vez más sobre las dos dimensiones a la vez.
 */
function totalGeneralDeHeatmap(
  ous: readonly HeatmapAxis[],
  funds: readonly HeatmapAxis[],
  lookup: Parameters<typeof sumaFilaVisible>[2],
): ExcelCellValue {
  const totales = funds.map((fund) => sumaColumnaVisible(fund.id, ous, lookup));
  const suma = totales.reduce((acumulado, total) => acumulado + total.suma, 0);
  return totales.some((total) => total.hayTapadas) ? `≥ ${suma}` : suma;
}
